import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Input,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import PageTitle from '../../components/PageTitle';
import { huifuToolsPaymentQuery } from '../../api';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

/** 汇付 trans_stat：S=成功已付，F=失败未付，P/I=处理中 */
const TRANS_STAT_MAP = {
  S: { color: 'success', text: '已支付（成功）', tip: '钱已收成功' },
  F: { color: 'error', text: '未支付（失败）', tip: '没付成 / 支付失败' },
  P: { color: 'processing', text: '处理中', tip: '尚未最终结果，可稍后重查' },
  I: { color: 'default', text: '初始化/处理中', tip: '尚未最终结果，可稍后重查' },
};

function resolveTransStat(stat) {
  const key = String(stat || '').trim().toUpperCase();
  return TRANS_STAT_MAP[key] || {
    color: 'default',
    text: key ? `未知状态 ${key}` : '无交易状态',
    tip: '请结合原始 JSON 判断',
  };
}

const MODE_OPTIONS = [
  { value: 'scanpay', label: '汇付查单个订单' },
  { value: 'local', label: '汇付查单个订单（仅系统内）' },
  { value: 'local_by_merchant', label: '按商户号查多个订单（仅系统内）' },
  { value: 'balance', label: '查商户余额' },
];

const SCANPAY_KEY_HINT = {
  org_hf_seq_id: '用汇付全局流水号（订单列表「汇付流水号」）',
  org_req_seq_id: '用请求流水号 + 原交易日期 org_req_date（yyyyMMdd）',
  party_order_id: '用支付凭证号 party_order_id',
  out_trans_id: '用微信/支付宝侧商户单号 out_trans_id',
};

const formatJson = (value) => {
  if (value == null) return '';
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const localColumns = [
  { title: '订单ID', dataIndex: 'id', width: 90 },
  { title: '订单号', dataIndex: 'order_no', width: 160, ellipsis: true },
  { title: '类型', dataIndex: 'order_type', width: 60 },
  { title: '支付状态', dataIndex: 'order_status', width: 80 },
  { title: '金额', dataIndex: 'order_amount', width: 90 },
  { title: 'huifu_id', dataIndex: 'pay_channel_huifu_id', width: 140, ellipsis: true },
  { title: 'hf_seq_id', dataIndex: 'pay_channel_hf_seq_id', width: 180, ellipsis: true },
  { title: 'req_seq_id', dataIndex: 'pay_channel_req_seq_id', width: 160, ellipsis: true },
  { title: '下单时间', dataIndex: 'create_time', width: 160 },
  { title: '支付时间', dataIndex: 'pay_time', width: 160, render: (v) => v || '-' },
];

const HuifuOrderQuery = () => {
  const [mode, setMode] = useState('scanpay');
  const [huifuId, setHuifuId] = useState('');
  const [orgHfSeqId, setOrgHfSeqId] = useState('');
  const [orgReqSeqId, setOrgReqSeqId] = useState('');
  const [orgReqDate, setOrgReqDate] = useState('');
  const [partyOrderId, setPartyOrderId] = useState('');
  const [outTransId, setOutTransId] = useState('');
  const [orderId, setOrderId] = useState('');
  const [orderNo, setOrderNo] = useState('');
  const [limit, setLimit] = useState('500');
  const [beginDate, setBeginDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const modeHint = useMemo(() => {
    if (mode === 'local') return '仅系统内：查本地 order.pay_channel_*，不调汇付。可用订单 ID / 订单号 / 流水号。';
    if (mode === 'local_by_merchant') return '仅系统内：按商户号列本地订单。可选手动填条数上限（1～1000，默认 500）与下单日期区间（yyyyMMdd）。';
    if (mode === 'balance') return '调汇付账户余额接口，看该商户当前余额（不是逐笔收款明细）。';
    return '向汇付查单个订单。可填流水号，也可先填本地订单 ID 自动带出通道字段。';
  }, [mode]);

  const runQuery = async () => {
    setLoading(true);
    setError('');
    setResult(null);
    const payload = {
      mode,
      huifu_id: String(huifuId || '').trim(),
      org_hf_seq_id: String(orgHfSeqId || '').trim(),
      org_req_seq_id: String(orgReqSeqId || '').trim(),
      org_req_date: String(orgReqDate || '').trim(),
      party_order_id: String(partyOrderId || '').trim(),
      out_trans_id: String(outTransId || '').trim(),
      order_no: String(orderNo || '').trim(),
      begin_date: String(beginDate || '').trim(),
      end_date: String(endDate || '').trim(),
    };
    const oid = String(orderId || '').trim();
    if (oid) {
      const n = Number(oid);
      if (!Number.isFinite(n) || n <= 0) {
        message.warning('订单 ID 须为正整数');
        setLoading(false);
        return;
      }
      payload.order_id = n;
    }
    const lim = String(limit || '').trim();
    if (lim) {
      const n = Number(lim);
      if (!Number.isFinite(n) || n <= 0) {
        message.warning('上限须为正整数（1～1000）');
        setLoading(false);
        return;
      }
      payload.limit = Math.min(1000, Math.floor(n));
    }

    try {
      const res = await huifuToolsPaymentQuery(payload);
      setResult(res || null);
      if (res?.success === false) {
        setError(res?.msg || '查询未成功，请看下方完整返回');
        message.warning(res?.msg || '查询未成功');
      } else {
        message.success(res?.msg || '查询完成');
      }
    } catch (e) {
      const msg = e?.response?.data?.msg || e?.response?.data?.error || e?.message || '查询失败';
      setError(msg);
      setResult(e?.response?.data || null);
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const localOrders = result?.data?.local?.orders || [];
  const channel = result?.data?.channel;
  const balance = result?.data?.balance;

  return (
    <div>
      <PageTitle title="汇付订单查询" />

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="手动核对汇付支付（不改订单状态）"
        description={
          <div>
            <div>{modeHint}</div>
            <div style={{ marginTop: 6 }}>
              扫码查单支持：{SCANPAY_KEY_HINT.org_hf_seq_id}；{SCANPAY_KEY_HINT.org_req_seq_id}；
              {SCANPAY_KEY_HINT.party_order_id}；{SCANPAY_KEY_HINT.out_trans_id}。下方 JSON 为完整返回。
            </div>
          </div>
        }
      />

      <Card title="查询条件" size="small" style={{ marginBottom: 16 }}>
        <Space direction="vertical" size="middle" style={{ width: '100%', maxWidth: 720 }}>
          <div>
            <Text type="secondary">查询方式</Text>
            <Select
              value={mode}
              options={MODE_OPTIONS}
              onChange={setMode}
              style={{ width: '100%' }}
              disabled={loading}
            />
          </div>

          <div>
            <Text type="secondary">汇付商户号 huifu_id</Text>
            <Input
              value={huifuId}
              onChange={(e) => setHuifuId(e.target.value)}
              placeholder="收款商户号；可用本地订单自动带出"
              disabled={loading}
            />
          </div>

          {mode === 'local_by_merchant' && (
            <>
              <div>
                <Text type="secondary">条数上限 limit（1～1000，默认 500）</Text>
                <Input
                  value={limit}
                  onChange={(e) => setLimit(e.target.value)}
                  placeholder="500"
                  disabled={loading}
                />
              </div>
              <div>
                <Text type="secondary">下单日起 begin_date（yyyyMMdd，可选）</Text>
                <Input
                  value={beginDate}
                  onChange={(e) => setBeginDate(e.target.value)}
                  placeholder="如 20260701"
                  disabled={loading}
                />
              </div>
              <div>
                <Text type="secondary">下单日止 end_date（yyyyMMdd，可选）</Text>
                <Input
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  placeholder="如 20260714"
                  disabled={loading}
                />
              </div>
            </>
          )}

          {(mode === 'scanpay' || mode === 'local') && (
            <>
              <div>
                <Text type="secondary">本地订单 ID（可选，优先反查通道字段）</Text>
                <Input
                  value={orderId}
                  onChange={(e) => setOrderId(e.target.value)}
                  placeholder="如 123456"
                  disabled={loading}
                />
              </div>
              <div>
                <Text type="secondary">本地订单号 order_no（可选）</Text>
                <Input
                  value={orderNo}
                  onChange={(e) => setOrderNo(e.target.value)}
                  placeholder="业务订单号"
                  disabled={loading}
                />
              </div>
              <div>
                <Text type="secondary">汇付全局流水号 org_hf_seq_id</Text>
                <Input
                  value={orgHfSeqId}
                  onChange={(e) => setOrgHfSeqId(e.target.value)}
                  placeholder="pay_channel_hf_seq_id"
                  disabled={loading}
                />
              </div>
              <div>
                <Text type="secondary">请求流水号 org_req_seq_id</Text>
                <Input
                  value={orgReqSeqId}
                  onChange={(e) => setOrgReqSeqId(e.target.value)}
                  placeholder="pay_channel_req_seq_id（jspay 的 order_id）"
                  disabled={loading}
                />
              </div>
              <div>
                <Text type="secondary">原交易日期 org_req_date（yyyyMMdd，按请求流水查时建议填）</Text>
                <Input
                  value={orgReqDate}
                  onChange={(e) => setOrgReqDate(e.target.value)}
                  placeholder="如 20260714；留空时会尝试用订单 create_time"
                  disabled={loading}
                />
              </div>
              {mode === 'scanpay' && (
                <>
                  <div>
                    <Text type="secondary">支付凭证号 party_order_id（可选）</Text>
                    <Input
                      value={partyOrderId}
                      onChange={(e) => setPartyOrderId(e.target.value)}
                      disabled={loading}
                    />
                  </div>
                  <div>
                    <Text type="secondary">微信/支付宝商户单号 out_trans_id（可选）</Text>
                    <Input
                      value={outTransId}
                      onChange={(e) => setOutTransId(e.target.value)}
                      disabled={loading}
                    />
                  </div>
                </>
              )}
            </>
          )}

          <Button type="primary" loading={loading} onClick={runQuery}>
            查询
          </Button>
        </Space>
      </Card>

      {error ? (
        <Alert type="error" showIcon style={{ marginBottom: 16 }} message={error} />
      ) : null}

      {localOrders.length > 0 ? (
        <Card title={`本地订单对照（${localOrders.length}）`} size="small" style={{ marginBottom: 16 }}>
          <Table
            size="small"
            rowKey={(r) => String(r.id)}
            columns={localColumns}
            dataSource={localOrders}
            pagination={false}
            scroll={{ x: 1400 }}
          />
        </Card>
      ) : null}

      {channel ? (
        <Card
          title={
            <Space wrap>
              <span>汇付查单结果</span>
              {(() => {
                const st = resolveTransStat(result?.data?.trans_stat);
                return <Tag color={st.color}>{st.text}</Tag>;
              })()}
              <Text type="secondary" style={{ fontSize: 12 }}>
                trans_stat={result?.data?.trans_stat || '-'} / resp_code={result?.data?.resp_code || '-'}
              </Text>
            </Space>
          }
          size="small"
          style={{ marginBottom: 16 }}
        >
          {(() => {
            const st = resolveTransStat(result?.data?.trans_stat);
            const alertType =
              st.color === 'success' ? 'success' : st.color === 'error' ? 'error' : 'info';
            return (
              <Alert
                showIcon
                type={alertType}
                style={{ marginBottom: 12 }}
                message={st.text}
                description={
                  <>
                    <div>{st.tip}</div>
                    <div style={{ marginTop: 4 }}>
                      接口返回：{result?.data?.resp_code || '-'} {result?.data?.resp_desc || ''}
                      （resp_code=00000000 只代表查单接口通了，是否付钱看 trans_stat）
                    </div>
                  </>
                }
              />
            );
          })()}
          <TextArea value={formatJson(channel)} autoSize={{ minRows: 10, maxRows: 28 }} readOnly />
        </Card>
      ) : null}

      {balance ? (
        <Card title="商户余额完整返回" size="small" style={{ marginBottom: 16 }}>
          <TextArea value={formatJson(balance)} autoSize={{ minRows: 8, maxRows: 24 }} readOnly />
        </Card>
      ) : null}

      {result ? (
        <Card title="完整响应（含 request / local / channel）" size="small">
          <TextArea value={formatJson(result)} autoSize={{ minRows: 12, maxRows: 36 }} readOnly />
        </Card>
      ) : null}
    </div>
  );
};

export default HuifuOrderQuery;
